import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "@/utils/env";
const com_logo = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/com_logo.png";

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setSuccess(false);

    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.error || "Failed to send reset email. Please try again.");
      }
    } catch (err) {
      setError("Failed to send reset email. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white relative flex flex-col items-center justify-center p-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>

      {/* Logo - Top Left */}
      <div className="absolute top-6 left-6 md:top-8 md:left-8">
        <img src={com_logo} alt="Connct Logo" className="h-7 md:h-8 w-auto" />
      </div>

      <div className="w-full max-w-[440px] flex flex-col gap-3 z-10 scale-95 md:scale-100">

        {/* Top Card - Heading */}
        <div className="bg-white border border-gray-400 rounded-[28px] px-6 py-6 text-center shadow-sm">
          <h1 className="text-2xl md:text-3xl font-bold text-black mb-1 leading-tight">
            Reset Your Password
          </h1>
          <p className="text-gray-600 mt-2 text-sm leading-relaxed">
            Enter your email address and we'll send you
            <br />
            a link to reset your password.
          </p>
        </div>

        {/* Bottom Card - Form */}
        <div className="bg-white border border-gray-400 rounded-[28px] px-6 py-6 shadow-sm">
          {!success ? (
            <>
              <h2 className="text-xl font-bold text-black mb-1">Forgot Password</h2>
              <p className="text-gray-600 mb-5 text-[13px]">
                We'll email you instructions to reset your password.
              </p>

              {error && (
                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-2 rounded-md text-xs mb-4">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block mb-2 text-sm text-gray-700">
                    Email Address<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="Enter Your Email Address"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </button>
              </form>

              <div className="mt-4 text-center">
                <Link to="/login" className="text-sm text-blue-600 hover:underline">
                  ← Back to Login
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h3 className="text-xl font-bold text-black mb-2">Check Your Email</h3>
              <p className="text-gray-600 text-sm mb-4">
                We've sent a password reset link to
                <br />
                <strong>{email}</strong>
              </p>
              <p className="text-gray-500 text-xs mb-6">
                The link will expire in 1 hour. If you don't see the email, check your spam folder.
              </p>

              <Link 
                to="/login"
                className="inline-block px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-all"
              >
                Return to Login
              </Link>

              <div className="mt-4">
                <button
                  onClick={() => {
                    setSuccess(false);
                    setEmail("");
                  }}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Send another email
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
